// Global Types
import type {
  $Config as $ApiServiceConfig,
} from '@vroskus/library-api';
import type {
  $AdminRouteRequest as $ApiRouteRequest,
  $AdminRouteResponse as $ApiRouteResponse,
} from 'common.types';

// Helpers
import Api from '@vroskus/library-api';
import _ from 'lodash';
import {
  z,
} from 'zod';

// Schemas
const objectSchema = z.object({
}).passthrough();
const arraySchema = z.array(objectSchema);
// const objectOrNullSchema = objectSchema.nullable();

// Types
export type $Config = $ApiServiceConfig;

class ApiService<C extends $ApiServiceConfig> extends Api<C> {
  // Auth
  async authenticate(
    params: $ApiRouteRequest<'/auth/authenticate'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/authenticate'>> {
    const response = await this.connection.post(
      '/auth/authenticate',
      params,
    );

    objectSchema.parse(response.data);

    return response.data;
  }

  async unauthenticate(): Promise<$ApiRouteResponse<'/auth/unauthenticate'>> {
    await this.connection.post('/auth/unauthenticate');
  }

  async forgotPassword(
    params: $ApiRouteRequest<'/auth/forgot_password'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/forgot_password'>> {
    const response = await this.connection.post(
      '/auth/forgot_password',
      params,
    );

    return response.data;
  }

  async resetPassword(
    params: $ApiRouteRequest<'/auth/reset_password'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/reset_password'>> {
    const response = await this.connection.post(
      '/auth/reset_password',
      params,
    );

    return response.data;
  }

  async getAuthUser(): Promise<$ApiRouteResponse<'/auth/user/get_one'>> {
    const response = await this.connection.post('/auth/user/get_one');

    return response.data;
  }

  async getManyAuthUserSocialUser(): Promise<$ApiRouteResponse<'/auth/user/get_many_social_user'>> {
    const response = await this.connection.post('/auth/user/get_many_social_user');

    arraySchema.parse(response.data);

    return response.data;
  }

  async getManyAuthUserRegattaInvoice(): Promise<$ApiRouteResponse<'/auth/user/get_many_regatta_invoice'>> {
    const response = await this.connection.post('/auth/user/get_many_regatta_invoice');

    arraySchema.parse(response.data);

    return response.data;
  }

  async updateAuthUser(
    params: $ApiRouteRequest<'/auth/user/update_one'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/user/update_one'>> {
    const response = await this.connection.post(
      '/auth/user/update_one',
      params,
    );

    return response.data;
  }

  async updateAuthUserPassword(
    params: $ApiRouteRequest<'/auth/user/update_password'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/user/update_password'>> {
    const response = await this.connection.post(
      '/auth/user/update_password',
      params,
    );

    return response.data;
  }

  async deleteAuthUser(): Promise<$ApiRouteResponse<'/auth/user/delete_one'>> {
    const response = await this.connection.post('/auth/user/delete_one');

    return response.data;
  }

  // Main (public)
  async getRegions(): Promise<$ApiRouteResponse<'/main/regions'>> {
    const response = await this.connection.post('/main/regions');

    arraySchema.parse(response.data);

    return response.data;
  }

  // Admin
  async getManyAdminAdmin(): Promise<$ApiRouteResponse<'/admin/admin/get_many'>> {
    const response = await this.connection.post('/admin/admin/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneAdminAdmin(
    params: $ApiRouteRequest<'/admin/admin/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/get_one'>> {
    const response = await this.connection.post(
      '/admin/admin/get_one',
      params,
    );

    return response.data;
  }

  async upsertAdminAdmin(
    params: $ApiRouteRequest<'/admin/admin/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/upsert'>> {
    const response = await this.connection.post(
      '/admin/admin/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminAdmin(
    params: $ApiRouteRequest<'/admin/admin/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/delete'>> {
    const response = await this.connection.post(
      '/admin/admin/delete',
      params,
    );

    return response.data;
  }

  // AdminAccessToken
  async getManyAdminAdminAccessToken(
    params: $ApiRouteRequest<'/admin/admin/access_token/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/access_token/get_many'>> {
    const response = await this.connection.post(
      '/admin/admin/access_token/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async updateAdminAdminAccessToken(
    params: $ApiRouteRequest<'/admin/admin/access_token/update'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/access_token/update'>> {
    const response = await this.connection.post(
      '/admin/admin/access_token/update',
      params,
    );

    return response.data;
  }

  async deleteAdminAdminAccessToken(
    params: $ApiRouteRequest<'/admin/admin/access_token/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/access_token/delete'>> {
    const response = await this.connection.post(
      '/admin/admin/access_token/delete',
      params,
    );

    return response.data;
  }

  // AdminSocialUser
  async getManyAdminAdminSocialUser(
    params: $ApiRouteRequest<'/admin/admin/social_user/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/social_user/get_many'>> {
    const response = await this.connection.post(
      '/admin/admin/social_user/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async updateAdminAdminSocialUser(
    params: $ApiRouteRequest<'/admin/admin/social_user/update'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/social_user/update'>> {
    const response = await this.connection.post(
      '/admin/admin/social_user/update',
      params,
    );

    return response.data;
  }

  async deleteAdminAdminSocialUser(
    params: $ApiRouteRequest<'/admin/admin/social_user/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/admin/social_user/delete'>> {
    const response = await this.connection.post(
      '/admin/admin/social_user/delete',
      params,
    );

    return response.data;
  }

  // Region
  async getManyAdminRegion(): Promise<$ApiRouteResponse<'/admin/region/get_many'>> {
    const response = await this.connection.post('/admin/region/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneAdminRegion(
    params: $ApiRouteRequest<'/admin/region/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/region/get_one'>> {
    const response = await this.connection.post(
      '/admin/region/get_one',
      params,
    );

    return response.data;
  }

  async upsertAdminRegion(
    params: $ApiRouteRequest<'/admin/region/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/region/upsert'>> {
    const response = await this.connection.post(
      '/admin/region/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminRegion(
    params: $ApiRouteRequest<'/admin/region/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/region/delete'>> {
    const response = await this.connection.post(
      '/admin/region/delete',
      params,
    );

    return response.data;
  }

  // RegionSeason
  async getManyAdminRegionSeason(
    params: $ApiRouteRequest<'/admin/region_season/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/region_season/get_many'>> {
    const response = await this.connection.post(
      '/admin/region_season/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminRegionSeason(
    params: $ApiRouteRequest<'/admin/region_season/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/region_season/upsert'>> {
    const response = await this.connection.post(
      '/admin/region_season/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminRegionSeason(
    params: $ApiRouteRequest<'/admin/region_season/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/region_season/delete'>> {
    const response = await this.connection.post(
      '/admin/region_season/delete',
      params,
    );

    return response.data;
  }

  // Crewmember
  async getManyAdminCrewmember(): Promise<$ApiRouteResponse<'/admin/crewmember/get_many'>> {
    const response = await this.connection.post('/admin/crewmember/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneAdminCrewmember(
    params: $ApiRouteRequest<'/admin/crewmember/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/get_one'>> {
    const response = await this.connection.post(
      '/admin/crewmember/get_one',
      params,
    );

    return response.data;
  }

  async upsertAdminCrewmember(
    params: $ApiRouteRequest<'/admin/crewmember/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/upsert'>> {
    const response = await this.connection.post(
      '/admin/crewmember/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminCrewmember(
    params: $ApiRouteRequest<'/admin/crewmember/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/delete'>> {
    const response = await this.connection.post(
      '/admin/crewmember/delete',
      params,
    );

    return response.data;
  }

  // CrewmemberAccessToken
  async getManyAdminCrewmemberAccessToken(
    params: $ApiRouteRequest<'/admin/crewmember/access_token/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/access_token/get_many'>> {
    const response = await this.connection.post(
      '/admin/crewmember/access_token/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async updateAdminCrewmemberAccessToken(
    params: $ApiRouteRequest<'/admin/crewmember/access_token/update'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/access_token/update'>> {
    const response = await this.connection.post(
      '/admin/crewmember/access_token/update',
      params,
    );

    return response.data;
  }

  async deleteAdminCrewmemberAccessToken(
    params: $ApiRouteRequest<'/admin/crewmember/access_token/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/access_token/delete'>> {
    const response = await this.connection.post(
      '/admin/crewmember/access_token/delete',
      params,
    );

    return response.data;
  }

  // CrewmemberSocialUser
  async getManyAdminCrewmemberSocialUser(
    params: $ApiRouteRequest<'/admin/crewmember/social_user/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/social_user/get_many'>> {
    const response = await this.connection.post(
      '/admin/crewmember/social_user/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async updateAdminCrewmemberSocialUser(
    params: $ApiRouteRequest<'/admin/crewmember/social_user/update'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/social_user/update'>> {
    const response = await this.connection.post(
      '/admin/crewmember/social_user/update',
      params,
    );

    return response.data;
  }

  async deleteAdminCrewmemberSocialUser(
    params: $ApiRouteRequest<'/admin/crewmember/social_user/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/crewmember/social_user/delete'>> {
    const response = await this.connection.post(
      '/admin/crewmember/social_user/delete',
      params,
    );

    return response.data;
  }

  // Advertisement
  async getManyAdminAdvertisement(
    params: $ApiRouteRequest<'/admin/advertisement/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/advertisement/get_many'>> {
    const response = await this.connection.post(
      '/admin/advertisement/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminAdvertisement(
    params: $ApiRouteRequest<'/admin/advertisement/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/advertisement/upsert'>> {
    const response = await this.connection.post(
      '/admin/advertisement/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminAdvertisement(
    params: $ApiRouteRequest<'/admin/advertisement/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/advertisement/delete'>> {
    const response = await this.connection.post(
      '/admin/advertisement/delete',
      params,
    );

    return response.data;
  }

  // Banner
  async getManyAdminBanner(): Promise<$ApiRouteResponse<'/admin/banner/get_many'>> {
    const response = await this.connection.post('/admin/banner/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminBanner(
    params: $ApiRouteRequest<'/admin/banner/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/banner/upsert'>> {
    const response = await this.connection.post(
      '/admin/banner/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminBanner(
    params: $ApiRouteRequest<'/admin/banner/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/banner/delete'>> {
    const response = await this.connection.post(
      '/admin/banner/delete',
      params,
    );

    return response.data;
  }

  // Group
  async getManyAdminGroup(): Promise<$ApiRouteResponse<'/admin/group/get_many'>> {
    const response = await this.connection.post('/admin/group/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminGroup(
    params: $ApiRouteRequest<'/admin/group/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/group/upsert'>> {
    const response = await this.connection.post(
      '/admin/group/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminGroup(
    params: $ApiRouteRequest<'/admin/group/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/group/delete'>> {
    const response = await this.connection.post(
      '/admin/group/delete',
      params,
    );

    return response.data;
  }

  async getResourceGroup(): Promise<$ApiRouteResponse<'/group/get_resource'>> {
    const response = await this.connection.post('/group/get_resource');

    return response.data;
  }

  // CustomPage
  async getManyAdminCustomPage(
    params: $ApiRouteRequest<'/admin/custom_page/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/custom_page/get_many'>> {
    const response = await this.connection.post(
      '/admin/custom_page/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminCustomPage(
    params: $ApiRouteRequest<'/admin/custom_page/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/custom_page/upsert'>> {
    const response = await this.connection.post(
      '/admin/custom_page/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminCustomPage(
    params: $ApiRouteRequest<'/admin/custom_page/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/custom_page/delete'>> {
    const response = await this.connection.post(
      '/admin/custom_page/delete',
      params,
    );

    return response.data;
  }

  // RegattaNotification
  async getManyAdminRegattaNotification(): Promise<$ApiRouteResponse<'/admin/regatta_notification/get_many'>> {
    const response = await this.connection.post('/admin/regatta_notification/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneAdminRegattaNotification(
    params: $ApiRouteRequest<'/admin/regatta_notification/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_notification/get_one'>> {
    const response = await this.connection.post(
      '/admin/regatta_notification/get_one',
      params,
    );

    return response.data;
  }

  // RegattaNotificationCrewmember
  async getManyAdminRegattaNotificationCrewmember(
    params: $ApiRouteRequest<'/admin/regatta_notification_crewmember/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_notification_crewmember/get_many'>> {
    const response = await this.connection.post(
      '/admin/regatta_notification_crewmember/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // RegattaEntry
  async findManyRegattaEntry(
    params: $ApiRouteRequest<'/regatta_entry/find_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry/find_many'>> {
    const response = await this.connection.post(
      '/regatta_entry/find_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getManyAdminRegattaEntry(
    params: $ApiRouteRequest<'/admin/regatta_entry/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_entry/get_many'>> {
    const response = await this.connection.post(
      '/admin/regatta_entry/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminRegattaEntry(
    params: $ApiRouteRequest<'/admin/regatta_entry/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_entry/upsert'>> {
    const response = await this.connection.post(
      '/admin/regatta_entry/upsert',
      params,
    );

    return response.data;
  }

  async addAdminRegattaEntry(
    params: $ApiRouteRequest<'/admin/regatta_entry/add'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_entry/add'>> {
    const response = await this.connection.post(
      '/admin/regatta_entry/add',
      params,
    );

    return response.data;
  }

  // RegattaEntryCrewmember
  async findManyRegattaEntryCrewmember(
    params: $ApiRouteRequest<'/regatta_entry_crewmember/find_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry_crewmember/find_many'>> {
    const response = await this.connection.post(
      '/regatta_entry_crewmember/find_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // Entry
  async getManyAdminEntry(
    params: $ApiRouteRequest<'/admin/entry/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/entry/get_many'>> {
    const response = await this.connection.post(
      '/admin/entry/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneAdminEntry(
    params: $ApiRouteRequest<'/admin/entry/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/entry/get_one'>> {
    const response = await this.connection.post(
      '/admin/entry/get_one',
      params,
    );

    return response.data;
  }

  async upsertAdminEntry(
    params: $ApiRouteRequest<'/admin/entry/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/entry/upsert'>> {
    const response = await this.connection.post(
      '/admin/entry/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminEntry(
    params: $ApiRouteRequest<'/admin/entry/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/entry/delete'>> {
    const response = await this.connection.post(
      '/admin/entry/delete',
      params,
    );

    return response.data;
  }

  // RegattaEntryRating
  async getManyRegattaEntryRating(
    params: $ApiRouteRequest<'/regatta_entry_rating/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry_rating/get_many'>> {
    const response = await this.connection.post(
      '/regatta_entry_rating/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async addRegattaEntryRating(
    params: $ApiRouteRequest<'/regatta_entry_rating/add'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry_rating/add'>> {
    const response = await this.connection.post(
      '/regatta_entry_rating/add',
      params,
    );

    return response.data;
  }

  async removeRegattaEntryRating(
    params: $ApiRouteRequest<'/regatta_entry_rating/remove'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry_rating/remove'>> {
    const response = await this.connection.post(
      '/regatta_entry_rating/remove',
      params,
    );

    return response.data;
  }

  async addLatestRegattaEntryRatings(
    params: $ApiRouteRequest<'/regatta_entry_rating/add_latest'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry_rating/add_latest'>> {
    const response = await this.connection.post(
      '/regatta_entry_rating/add_latest',
      params,
    );

    return response.data;
  }

  // Rating
  async createRating(
    params: $ApiRouteRequest<'/rating/create'>['body'],
  ): Promise<$ApiRouteResponse<'/rating/create'>> {
    const response = await this.connection.post(
      '/rating/create',
      params,
    );

    return response.data;
  }

  async getManyAdminRating(
    params: $ApiRouteRequest<'/admin/rating/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/rating/get_many'>> {
    const response = await this.connection.post(
      '/admin/rating/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // EntryRating
  async getManyAdminEntryRating(
    params: $ApiRouteRequest<'/admin/entry_rating/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/entry_rating/get_many'>> {
    const response = await this.connection.post(
      '/admin/entry_rating/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async addAdminEntryRating(
    params: $ApiRouteRequest<'/admin/entry_rating/add'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/entry_rating/add'>> {
    const response = await this.connection.post(
      '/admin/entry_rating/add',
      params,
    );

    return response.data;
  }

  // RegattaDocument
  async getManyAdminRegattaDocument(): Promise<$ApiRouteResponse<'/admin/regatta_document/get_many'>> {
    const response = await this.connection.post('/admin/regatta_document/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminRegattaDocument(
    params: $ApiRouteRequest<'/admin/regatta_document/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_document/upsert'>> {
    const response = await this.connection.post(
      '/admin/regatta_document/upsert',
      params,
    );

    return response.data;
  }

  // ChampionshipDocument
  async getManyAdminChampionshipDocument(): Promise<$ApiRouteResponse<'/admin/championship_document/get_many'>> {
    const response = await this.connection.post('/admin/championship_document/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminChampionshipDocument(
    params: $ApiRouteRequest<'/admin/championship_document/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/championship_document/upsert'>> {
    const response = await this.connection.post(
      '/admin/championship_document/upsert',
      params,
    );

    return response.data;
  }

  // Championship
  async getResourceChampionship(
    params: $ApiRouteRequest<'/championship/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/championship/get_resource'>> {
    const response = await this.connection.post(
      '/championship/get_resource',
      params,
    );

    return response.data;
  }

  async getManyChampionship(
    params: $ApiRouteRequest<'/championship/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship/get_many'>> {
    const response = await this.connection.post(
      '/championship/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneChampionship(
    params: $ApiRouteRequest<'/championship/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/championship/get_one'>> {
    const response = await this.connection.post(
      '/championship/get_one',
      params,
    );

    return response.data;
  }

  async upsertChampionship(
    params: $ApiRouteRequest<'/championship/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship/upsert'>> {
    const response = await this.connection.post(
      '/championship/upsert',
      params,
    );

    return response.data;
  }

  async deleteChampionship(
    params: $ApiRouteRequest<'/championship/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship/delete'>> {
    const response = await this.connection.post(
      '/championship/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipAdmin
  async getManyChampionshipAdmin(
    params: $ApiRouteRequest<'/championship_admin/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin/get_many'>> {
    const response = await this.connection.post(
      '/championship_admin/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async addChampionshipAdmin(
    params: $ApiRouteRequest<'/championship_admin/add'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin/add'>> {
    const response = await this.connection.post(
      '/championship_admin/add',
      params,
    );

    return response.data;
  }

  async removeChampionshipAdmin(
    params: $ApiRouteRequest<'/championship_admin/remove'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin/remove'>> {
    const response = await this.connection.post(
      '/championship_admin/remove',
      params,
    );

    return response.data;
  }

  // ChampionshipAdminRequest
  async getResourceChampionshipAdminRequest(
    params: $ApiRouteRequest<'/championship_admin_request/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin_request/get_resource'>> {
    const response = await this.connection.post(
      '/championship_admin_request/get_resource',
      params,
    );

    return response.data;
  }

  async getManyChampionshipAdminRequest(
    params: $ApiRouteRequest<'/championship_admin_request/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin_request/get_many'>> {
    const response = await this.connection.post(
      '/championship_admin_request/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getManyChampionshipChampionshipAdminRequest(
    params: $ApiRouteRequest<'/championship_admin_request/get_many_by_championship'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin_request/get_many_by_championship'>> {
    const response = await this.connection.post(
      '/championship_admin_request/get_many_by_championship',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertChampionshipAdminRequest(
    params: $ApiRouteRequest<'/championship_admin_request/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin_request/upsert'>> {
    const response = await this.connection.post(
      '/championship_admin_request/upsert',
      params,
    );

    return response.data;
  }

  async deleteChampionshipAdminRequest(
    params: $ApiRouteRequest<'/championship_admin_request/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_admin_request/delete'>> {
    const response = await this.connection.post(
      '/championship_admin_request/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipRegatta
  async getManyChampionshipRegatta(
    params: $ApiRouteRequest<'/championship_regatta/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_regatta/get_many'>> {
    const response = await this.connection.post(
      '/championship_regatta/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async addChampionshipRegatta(
    params: $ApiRouteRequest<'/championship_regatta/add'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_regatta/add'>> {
    const response = await this.connection.post(
      '/championship_regatta/add',
      params,
    );

    return response.data;
  }

  async removeChampionshipRegatta(
    params: $ApiRouteRequest<'/championship_regatta/remove'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_regatta/remove'>> {
    const response = await this.connection.post(
      '/championship_regatta/remove',
      params,
    );

    return response.data;
  }

  // ChampionshipSponsor
  async getManyChampionshipSponsor(
    params: $ApiRouteRequest<'/championship_sponsor/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_sponsor/get_many'>> {
    const response = await this.connection.post(
      '/championship_sponsor/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertChampionshipSponsor(
    params: $ApiRouteRequest<'/championship_sponsor/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_sponsor/upsert'>> {
    const response = await this.connection.post(
      '/championship_sponsor/upsert',
      params,
    );

    return response.data;
  }

  async deleteChampionshipSponsor(
    params: $ApiRouteRequest<'/championship_sponsor/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_sponsor/delete'>> {
    const response = await this.connection.post(
      '/championship_sponsor/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipDocument
  async getManyChampionshipDocument(
    params: $ApiRouteRequest<'/championship_document/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_document/get_many'>> {
    const response = await this.connection.post(
      '/championship_document/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertChampionshipDocument(
    params: $ApiRouteRequest<'/championship_document/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_document/upsert'>> {
    const response = await this.connection.post(
      '/championship_document/upsert',
      params,
    );

    return response.data;
  }

  async deleteChampionshipDocument(
    params: $ApiRouteRequest<'/championship_document/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_document/delete'>> {
    const response = await this.connection.post(
      '/championship_document/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipResultTable
  async getManyChampionshipResultTable(
    params: $ApiRouteRequest<'/championship_result_table/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table/get_many'>> {
    const response = await this.connection.post(
      '/championship_result_table/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertChampionshipResultTable(
    params: $ApiRouteRequest<'/championship_result_table/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table/upsert'>> {
    const optimisedParams = _.omit(
      params,
      [
        'ChampionshipResultTableEntries',
        'ChampionshipResultTableRaces',
      ],
    );

    const response = await this.connection.post(
      '/championship_result_table/upsert',
      optimisedParams,
    );

    return response.data;
  }

  async deleteChampionshipResultTable(
    params: $ApiRouteRequest<'/championship_result_table/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table/delete'>> {
    const response = await this.connection.post(
      '/championship_result_table/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipResultTableEntry
  async upsertChampionshipResultTableEntry(
    params: $ApiRouteRequest<'/championship_result_table_entry/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table_entry/upsert'>> {
    const response = await this.connection.post(
      '/championship_result_table_entry/upsert',
      params,
    );

    return response.data;
  }

  async deleteChampionshipResultTableEntry(
    params: $ApiRouteRequest<'/championship_result_table_entry/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table_entry/delete'>> {
    const response = await this.connection.post(
      '/championship_result_table_entry/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipResultTableRace
  async upsertChampionshipResultTableRace(
    params: $ApiRouteRequest<'/championship_result_table_race/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table_race/upsert'>> {
    const response = await this.connection.post(
      '/championship_result_table_race/upsert',
      params,
    );

    return response.data;
  }

  async deleteChampionshipResultTableRace(
    params: $ApiRouteRequest<'/championship_result_table_race/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table_race/delete'>> {
    const response = await this.connection.post(
      '/championship_result_table_race/delete',
      params,
    );

    return response.data;
  }

  // ChampionshipResultTableRaceResult
  async updateChampionshipResultTableRaceResult(
    params: $ApiRouteRequest<'/championship_result_table_race_result/update'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_result_table_race_result/update'>> {
    const response = await this.connection.post(
      '/championship_result_table_race_result/update',
      params,
    );

    return response.data;
  }

  // ChampionshipRegatta
  async getResourceChampionshipRegatta(
    params: $ApiRouteRequest<'/championship_regatta/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/championship_regatta/get_resource'>> {
    const response = await this.connection.post(
      '/championship_regatta/get_resource',
      params,
    );

    return response.data;
  }

  // Regatta
  async getResourceRegatta(
    params: $ApiRouteRequest<'/regatta/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta/get_resource'>> {
    const response = await this.connection.post(
      '/regatta/get_resource',
      params,
    );

    return response.data;
  }

  async getManyRegatta(
    params: $ApiRouteRequest<'/regatta/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta/get_many'>> {
    const response = await this.connection.post(
      '/regatta/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneRegatta(
    params: $ApiRouteRequest<'/regatta/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta/get_one'>> {
    const response = await this.connection.post(
      '/regatta/get_one',
      params,
    );

    return response.data;
  }

  async createRegatta(
    params: $ApiRouteRequest<'/regatta/create'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta/create'>> {
    const response = await this.connection.post(
      '/regatta/create',
      params,
    );

    return response.data;
  }

  async updateRegatta(
    params: $ApiRouteRequest<'/regatta/update'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta/update'>> {
    const response = await this.connection.post(
      '/regatta/update',
      params,
    );

    return response.data;
  }

  async deleteRegatta(
    params: $ApiRouteRequest<'/regatta/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta/delete'>> {
    const response = await this.connection.post(
      '/regatta/delete',
      params,
    );

    return response.data;
  }

  // RegattaGroup
  async getResourceRegattaGroup(
    params: $ApiRouteRequest<'/regatta_group/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_group/get_resource'>> {
    const response = await this.connection.post(
      '/regatta_group/get_resource',
      params,
    );

    return response.data;
  }

  async getManyRegattaGroup(
    params: $ApiRouteRequest<'/regatta_group/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_group/get_many'>> {
    const response = await this.connection.post(
      '/regatta_group/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaGroup(
    params: $ApiRouteRequest<'/regatta_group/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_group/upsert'>> {
    const response = await this.connection.post(
      '/regatta_group/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaGroup(
    params: $ApiRouteRequest<'/regatta_group/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_group/delete'>> {
    const response = await this.connection.post(
      '/regatta_group/delete',
      params,
    );

    return response.data;
  }

  // RegattaEntry
  async getResourceRegattaEntry(
    params: $ApiRouteRequest<'/regatta_entry/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry/get_resource'>> {
    const response = await this.connection.post(
      '/regatta_entry/get_resource',
      params,
    );

    return response.data;
  }

  async getManyRegattaEntry(
    params: $ApiRouteRequest<'/regatta_entry/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry/get_many'>> {
    const response = await this.connection.post(
      '/regatta_entry/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getOneRegattaEntry(
    params: $ApiRouteRequest<'/regatta_entry/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry/get_one'>> {
    const response = await this.connection.post(
      '/regatta_entry/get_one',
      params,
    );

    return response.data;
  }

  async upsertRegattaEntry(
    params: $ApiRouteRequest<'/regatta_entry/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry/upsert'>> {
    const response = await this.connection.post(
      '/regatta_entry/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaEntry(
    params: $ApiRouteRequest<'/regatta_entry/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry/delete'>> {
    const response = await this.connection.post(
      '/regatta_entry/delete',
      params,
    );

    return response.data;
  }

  // Entry
  async findManyEntry(
    params: $ApiRouteRequest<'/entry/find_many'>['body'],
  ): Promise<$ApiRouteResponse<'/entry/find_many'>> {
    const response = await this.connection.post(
      '/entry/find_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // RegattaContact
  async getManyRegattaContact(
    params: $ApiRouteRequest<'/regatta_contact/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_contact/get_many'>> {
    const response = await this.connection.post(
      '/regatta_contact/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaContact(
    params: $ApiRouteRequest<'/regatta_contact/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_contact/upsert'>> {
    const response = await this.connection.post(
      '/regatta_contact/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaContact(
    params: $ApiRouteRequest<'/regatta_contact/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_contact/delete'>> {
    const response = await this.connection.post(
      '/regatta_contact/delete',
      params,
    );

    return response.data;
  }

  // RegattaSponsor
  async getManyRegattaSponsor(
    params: $ApiRouteRequest<'/regatta_sponsor/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_sponsor/get_many'>> {
    const response = await this.connection.post(
      '/regatta_sponsor/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaSponsor(
    params: $ApiRouteRequest<'/regatta_sponsor/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_sponsor/upsert'>> {
    const response = await this.connection.post(
      '/regatta_sponsor/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaSponsor(
    params: $ApiRouteRequest<'/regatta_sponsor/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_sponsor/delete'>> {
    const response = await this.connection.post(
      '/regatta_sponsor/delete',
      params,
    );

    return response.data;
  }

  // RegattaDocument
  async getManyRegattaDocument(
    params: $ApiRouteRequest<'/regatta_document/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_document/get_many'>> {
    const response = await this.connection.post(
      '/regatta_document/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaDocument(
    params: $ApiRouteRequest<'/regatta_document/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_document/upsert'>> {
    const response = await this.connection.post(
      '/regatta_document/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaDocument(
    params: $ApiRouteRequest<'/regatta_document/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_document/delete'>> {
    const response = await this.connection.post(
      '/regatta_document/delete',
      params,
    );

    return response.data;
  }

  // RegattaMessage
  async getManyRegattaMessage(
    params: $ApiRouteRequest<'/regatta_message/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_message/get_many'>> {
    const response = await this.connection.post(
      '/regatta_message/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaMessage(
    params: $ApiRouteRequest<'/regatta_message/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_message/upsert'>> {
    const response = await this.connection.post(
      '/regatta_message/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaMessage(
    params: $ApiRouteRequest<'/regatta_message/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_message/delete'>> {
    const response = await this.connection.post(
      '/regatta_message/delete',
      params,
    );

    return response.data;
  }

  // RegattaDay
  async getResourceRegattaDay(
    params: $ApiRouteRequest<'/regatta_day/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_day/get_resource'>> {
    const response = await this.connection.post(
      '/regatta_day/get_resource',
      params,
    );

    return response.data;
  }

  async updateRegattaDay(
    params: $ApiRouteRequest<'/regatta_day/update'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_day/update'>> {
    const response = await this.connection.post(
      '/regatta_day/update',
      params,
    );

    return response.data;
  }

  // RegattaProtest
  async getManyRegattaProtest(
    params: $ApiRouteRequest<'/regatta_protest/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_protest/get_many'>> {
    const response = await this.connection.post(
      '/regatta_protest/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaProtest(
    params: $ApiRouteRequest<'/regatta_protest/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_protest/upsert'>> {
    const response = await this.connection.post(
      '/regatta_protest/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaProtest(
    params: $ApiRouteRequest<'/regatta_protest/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_protest/delete'>> {
    const response = await this.connection.post(
      '/regatta_protest/delete',
      params,
    );

    return response.data;
  }

  // RegattaProtestEnd
  async getManyRegattaProtestEnd(
    params: $ApiRouteRequest<'/regatta_protest_end/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_protest_end/get_many'>> {
    const response = await this.connection.post(
      '/regatta_protest_end/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaProtestEnd(
    params: $ApiRouteRequest<'/regatta_protest_end/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_protest_end/upsert'>> {
    const response = await this.connection.post(
      '/regatta_protest_end/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaProtestEnd(
    params: $ApiRouteRequest<'/regatta_protest_end/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_protest_end/delete'>> {
    const response = await this.connection.post(
      '/regatta_protest_end/delete',
      params,
    );

    return response.data;
  }

  // RegattaSchedule
  async getManyRegattaSchedule(
    params: $ApiRouteRequest<'/regatta_schedule/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_schedule/get_many'>> {
    const response = await this.connection.post(
      '/regatta_schedule/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaSchedule(
    params: $ApiRouteRequest<'/regatta_schedule/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_schedule/upsert'>> {
    const response = await this.connection.post(
      '/regatta_schedule/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaSchedule(
    params: $ApiRouteRequest<'/regatta_schedule/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_schedule/delete'>> {
    const response = await this.connection.post(
      '/regatta_schedule/delete',
      params,
    );

    return response.data;
  }

  // RegattaResultTable
  async getResourceRegattaResultTable(
    params: $ApiRouteRequest<'/regatta_result_table/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table/get_resource'>> {
    const response = await this.connection.post(
      '/regatta_result_table/get_resource',
      params,
    );

    return response.data;
  }

  async getManyRegattaResultTable(
    params: $ApiRouteRequest<'/regatta_result_table/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table/get_many'>> {
    const response = await this.connection.post(
      '/regatta_result_table/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaResultTable(
    params: $ApiRouteRequest<'/regatta_result_table/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table/upsert'>> {
    const response = await this.connection.post(
      '/regatta_result_table/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaResultTable(
    params: $ApiRouteRequest<'/regatta_result_table/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table/delete'>> {
    const response = await this.connection.post(
      '/regatta_result_table/delete',
      params,
    );

    return response.data;
  }

  // RegattaResultTableEntry
  async upsertRegattaResultTableEntry(
    params: $ApiRouteRequest<'/regatta_result_table_entry/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table_entry/upsert'>> {
    const response = await this.connection.post(
      '/regatta_result_table_entry/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaResultTableEntry(
    params: $ApiRouteRequest<'/regatta_result_table_entry/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table_entry/delete'>> {
    const response = await this.connection.post(
      '/regatta_result_table_entry/delete',
      params,
    );

    return response.data;
  }

  // RegattaResultTableRace
  async upsertRegattaResultTableRace(
    params: $ApiRouteRequest<'/regatta_result_table_race/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table_race/upsert'>> {
    const response = await this.connection.post(
      '/regatta_result_table_race/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaResultTableRace(
    params: $ApiRouteRequest<'/regatta_result_table_race/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table_race/delete'>> {
    const response = await this.connection.post(
      '/regatta_result_table_race/delete',
      params,
    );

    return response.data;
  }

  // RegattaResultTableRaceResult
  async updateRegattaResultTableRaceResult(
    params: $ApiRouteRequest<'/regatta_result_table_race_result/update'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_table_race_result/update'>> {
    const optimisedParams = _.omit(
      params,
      [
        'RegattaResultTable',
        'RegattaResultTableRace',
        'RegattaResultTableEntry',
      ],
    );

    const response = await this.connection.post(
      '/regatta_result_table_race_result/update',
      optimisedParams,
    );

    return response.data;
  }

  // RegattaResultOverall
  async getManyRegattaResultOverall(
    params: $ApiRouteRequest<'/regatta_result_overall/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_overall/get_many'>> {
    const response = await this.connection.post(
      '/regatta_result_overall/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaResultOverall(
    params: $ApiRouteRequest<'/regatta_result_overall/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_overall/upsert'>> {
    const response = await this.connection.post(
      '/regatta_result_overall/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaResultOverall(
    params: $ApiRouteRequest<'/regatta_result_overall/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_overall/delete'>> {
    const response = await this.connection.post(
      '/regatta_result_overall/delete',
      params,
    );

    return response.data;
  }

  // RegattaResultDiploma
  async getManyRegattaResultDiploma(
    params: $ApiRouteRequest<'/regatta_result_diploma/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_diploma/get_many'>> {
    const response = await this.connection.post(
      '/regatta_result_diploma/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaResultDiploma(
    params: $ApiRouteRequest<'/regatta_result_diploma/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_diploma/upsert'>> {
    const response = await this.connection.post(
      '/regatta_result_diploma/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaResultDiploma(
    params: $ApiRouteRequest<'/regatta_result_diploma/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_result_diploma/delete'>> {
    const response = await this.connection.post(
      '/regatta_result_diploma/delete',
      params,
    );

    return response.data;
  }

  // RegattaAdmin
  async getManyRegattaAdmin(
    params: $ApiRouteRequest<'/regatta_admin/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin/get_many'>> {
    const response = await this.connection.post(
      '/regatta_admin/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async addRegattaAdmin(
    params: $ApiRouteRequest<'/regatta_admin/add'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin/add'>> {
    const response = await this.connection.post(
      '/regatta_admin/add',
      params,
    );

    return response.data;
  }

  async removeRegattaAdmin(
    params: $ApiRouteRequest<'/regatta_admin/remove'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin/remove'>> {
    const response = await this.connection.post(
      '/regatta_admin/remove',
      params,
    );

    return response.data;
  }

  // RegattaAdminRequest
  async getResourceRegattaAdminRequest(
    params: $ApiRouteRequest<'/regatta_admin_request/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin_request/get_resource'>> {
    const response = await this.connection.post(
      '/regatta_admin_request/get_resource',
      params,
    );

    return response.data;
  }

  async getManyRegattaRegattaAdminRequest(
    params: $ApiRouteRequest<'/regatta_admin_request/get_many_by_regatta'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin_request/get_many_by_regatta'>> {
    const response = await this.connection.post(
      '/regatta_admin_request/get_many_by_regatta',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getManyRegattaAdminRequest(
    params: $ApiRouteRequest<'/regatta_admin_request/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin_request/get_many'>> {
    const response = await this.connection.post(
      '/regatta_admin_request/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaAdminRequest(
    params: $ApiRouteRequest<'/regatta_admin_request/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin_request/upsert'>> {
    const response = await this.connection.post(
      '/regatta_admin_request/upsert',
      params,
    );

    return response.data;
  }

  async revokeRegattaAdminRequest(
    params: $ApiRouteRequest<'/regatta_admin_request/revoke'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin_request/revoke'>> {
    const response = await this.connection.post(
      '/regatta_admin_request/revoke',
      params,
    );

    return response.data;
  }

  async rejectRegattaAdminRequest(
    params: $ApiRouteRequest<'/regatta_admin_request/reject'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_admin_request/reject'>> {
    const response = await this.connection.post(
      '/regatta_admin_request/reject',
      params,
    );

    return response.data;
  }

  // RegattaRequirement
  async getManyRegattaRequirement(
    params: $ApiRouteRequest<'/regatta_requirement/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_requirement/get_many'>> {
    const response = await this.connection.post(
      '/regatta_requirement/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaRequirement(
    params: $ApiRouteRequest<'/regatta_requirement/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_requirement/upsert'>> {
    const response = await this.connection.post(
      '/regatta_requirement/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaRequirement(
    params: $ApiRouteRequest<'/regatta_requirement/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_requirement/delete'>> {
    const response = await this.connection.post(
      '/regatta_requirement/delete',
      params,
    );

    return response.data;
  }

  // RegattaCheck
  async getManyRegattaCheck(
    params: $ApiRouteRequest<'/regatta_check/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_check/get_many'>> {
    const response = await this.connection.post(
      '/regatta_check/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaCheck(
    params: $ApiRouteRequest<'/regatta_check/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_check/upsert'>> {
    const response = await this.connection.post(
      '/regatta_check/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaCheck(
    params: $ApiRouteRequest<'/regatta_check/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_check/delete'>> {
    const response = await this.connection.post(
      '/regatta_check/delete',
      params,
    );

    return response.data;
  }

  // RegattaEntryCheck
  async updateRegattaEntryCheck(
    params: $ApiRouteRequest<'/regatta_entry_check/update'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_entry_check/update'>> {
    const response = await this.connection.post(
      '/regatta_entry_check/update',
      params,
    );

    return response.data;
  }

  // RegattaBoatRent
  async getManyRegattaBoatRent(
    params: $ApiRouteRequest<'/regatta_boat_rent/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_boat_rent/get_many'>> {
    const response = await this.connection.post(
      '/regatta_boat_rent/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaBoatRent(
    params: $ApiRouteRequest<'/regatta_boat_rent/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_boat_rent/upsert'>> {
    const response = await this.connection.post(
      '/regatta_boat_rent/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaBoatRent(
    params: $ApiRouteRequest<'/regatta_boat_rent/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_boat_rent/delete'>> {
    const response = await this.connection.post(
      '/regatta_boat_rent/delete',
      params,
    );

    return response.data;
  }

  // RegattaCourse
  async getOneRegattaCourse(
    params: $ApiRouteRequest<'/regatta_course/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course/get_one'>> {
    const response = await this.connection.post(
      '/regatta_course/get_one',
      params,
    );

    return response.data;
  }

  async getManyRegattaCourse(
    params: $ApiRouteRequest<'/regatta_course/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course/get_many'>> {
    const response = await this.connection.post(
      '/regatta_course/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaCourse(
    params: $ApiRouteRequest<'/regatta_course/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course/upsert'>> {
    const response = await this.connection.post(
      '/regatta_course/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaCourse(
    params: $ApiRouteRequest<'/regatta_course/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course/delete'>> {
    const response = await this.connection.post(
      '/regatta_course/delete',
      params,
    );

    return response.data;
  }

  // RegattaCourseRace
  async upsertRegattaCourseRace(
    params: $ApiRouteRequest<'/regatta_course_race/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course_race/upsert'>> {
    const response = await this.connection.post(
      '/regatta_course_race/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaCourseRace(
    params: $ApiRouteRequest<'/regatta_course_race/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course_race/delete'>> {
    const response = await this.connection.post(
      '/regatta_course_race/delete',
      params,
    );

    return response.data;
  }

  // RegattaCoursePoint
  async getManyRegattaCoursePoint(
    params: $ApiRouteRequest<'/regatta_course_point/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course_point/get_many'>> {
    const response = await this.connection.post(
      '/regatta_course_point/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertRegattaCoursePoint(
    params: $ApiRouteRequest<'/regatta_course_point/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course_point/upsert'>> {
    const response = await this.connection.post(
      '/regatta_course_point/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaCoursePoint(
    params: $ApiRouteRequest<'/regatta_course_point/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course_point/delete'>> {
    const response = await this.connection.post(
      '/regatta_course_point/delete',
      params,
    );

    return response.data;
  }

  async sortRegattaCoursePoint(
    params: $ApiRouteRequest<'/regatta_course_point/sort'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_course_point/sort'>> {
    const response = await this.connection.post(
      '/regatta_course_point/sort',
      params,
    );

    return response.data;
  }

  // RegattaLog
  async getManyRegattaLog(
    params: $ApiRouteRequest<'/regatta_log/get_many'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_log/get_many'>> {
    const response = await this.connection.post(
      '/regatta_log/get_many',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // RegattaPayment
  async findOneRegattaPayment(
    params: $ApiRouteRequest<'/regatta_payment/find_one'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_payment/find_one'>> {
    const response = await this.connection.post(
      '/regatta_payment/find_one',
      params,
    );

    return response.data;
  }

  async upsertRegattaPayment(
    params: $ApiRouteRequest<'/regatta_payment/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_payment/upsert'>> {
    const response = await this.connection.post(
      '/regatta_payment/upsert',
      params,
    );

    return response.data;
  }

  async getManyAdminRegattaPayment(): Promise<$ApiRouteResponse<'/admin/regatta_payment/get_many'>> {
    const response = await this.connection.post('/admin/regatta_payment/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminRegattaPayment(
    params: $ApiRouteRequest<'/admin/regatta_payment/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_payment/upsert'>> {
    const response = await this.connection.post(
      '/admin/regatta_payment/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminRegattaPayment(
    params: $ApiRouteRequest<'/admin/regatta_payment/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_payment/delete'>> {
    const response = await this.connection.post(
      '/admin/regatta_payment/delete',
      params,
    );

    return response.data;
  }

  // RegattaPaymentDiscount
  async getOneRegattaPaymentDiscount(
    params: $ApiRouteRequest<'/regatta_payment_discount/get_one'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_payment_discount/get_one'>> {
    const response = await this.connection.post(
      '/regatta_payment_discount/get_one',
      params,
    );

    return response.data;
  }

  async getLastRegattaPaymentDiscount(
    params: $ApiRouteRequest<'/regatta_payment_discount/get_last'>['body'],
  ): Promise<$ApiRouteResponse<'/regatta_payment_discount/get_last'>> {
    const response = await this.connection.post(
      '/regatta_payment_discount/get_last',
      params,
    );

    return response.data;
  }

  async getManyAdminRegattaPaymentDiscount(): Promise<$ApiRouteResponse<'/admin/regatta_payment_discount/get_many'>> {
    const response = await this.connection.post('/admin/regatta_payment_discount/get_many');

    arraySchema.parse(response.data);

    return response.data;
  }

  async upsertAdminRegattaPaymentDiscount(
    params: $ApiRouteRequest<'/admin/regatta_payment_discount/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_payment_discount/upsert'>> {
    const response = await this.connection.post(
      '/admin/regatta_payment_discount/upsert',
      params,
    );

    return response.data;
  }

  async deleteAdminRegattaPaymentDiscount(
    params: $ApiRouteRequest<'/admin/regatta_payment_discount/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/admin/regatta_payment_discount/delete'>> {
    const response = await this.connection.post(
      '/admin/regatta_payment_discount/delete',
      params,
    );

    return response.data;
  }

  // Utilities
  async utilityUpdateRatings(
    params: $ApiRouteRequest<'/utility/update_ratings'>['body'],
  ): Promise<$ApiRouteResponse<'/utility/update_ratings'>> {
    await this.connection.post(
      '/utility/update_ratings',
      params,
    );
  }
}

export default ApiService;
